import axios from "axios";
import jwt_decode from "jwt-decode";
import Vue from "vue";

export const state = {
  role: [],
  user: null,
  accessToken: null,
  isAuthenticate: !!localStorage.getItem("isAuthenticate")
};

export const mutations = {
  /**
   *
   * @param {{role: {id: string; name: string}[]; user:object}} state
   * @param {object} payload
   */
  setUser(state, payload) {
    state.user = payload;
  },
  setToken(state, payload) {
    state.accessToken = payload;
  },
  /**
   *
   * @param {{role: any[]; user: object}} state
   * @param {object[]} payload
   */
  setRole(state, payload) {
    state.role = payload;
  },
  clearRole(state) {
    state.role = [];
  },
  clearUser(state) {
    state.user = null;
  },
  clearToken(state) {
    state.accessToken = null;
  },
  setIsAuthenticate(state, status) {
    state.isAuthenticate = status;
    if (status) {
      localStorage.setItem("isAuthenticate", status);
    } else {
      localStorage.removeItem("isAuthenticate");
    }
  }
};

export const actions = {
  setUser({ commit }, user) {
    commit("setUser", user);
  },
  /**
   *
   * @param {*} param0
   * @param {{id: string; name: string}[]} roles
   */
  setRole({ commit }, roles) {
    commit("setRole", roles);
  },
  clearRole({ commit }) {
    commit("clearRole");
  },
  clearUser({ commit }) {
    commit("clearUser");
  },
  loginDigitalId() {
    window.location.href = `${process.env.VUE_APP_SMEGP_BASE_API}/auth/digitalid/login?backToPath=${window.location.origin}/main`;
  },
  loginDbdId() {
    window.location.href = `${process.env.VUE_APP_SMEGP_BASE_API}/auth/dbd/login?backToPath=${window.location.origin}/main`;
  },
  setUserFromToken({ commit }, payload) {
    const decodeValue = jwt_decode(payload);
    commit("setUser", decodeValue);
  },
  setRoleFromToken({ commit }, payload) {
    const decodeValue = jwt_decode(payload);
    commit("setRole", decodeValue.USER_ROLE);
  },
  async loginViaSso({ commit, dispatch }, ssoToken) {
    // Destination flow — verify the one-time SSO token, mint our own JWT,
    // and set Vuex/localStorage state so the navbar reflects the new user.
    // Caller (sso/landing.vue) handles redirect on success and routes to
    // /sso/error on failure.
    const res = await axios.post(
      `/auth/sso/verify-token`,
      { token: ssoToken },
      { withCredentials: true }
    );
    const { accessToken, redirectPath } = res.data || {};
    if (!accessToken) {
      throw new Error("missing_access_token");
    }
    commit("setToken", accessToken);
    dispatch("setUserFromToken", accessToken);
    dispatch("setRoleFromToken", accessToken);
    commit("setIsAuthenticate", true);
    if (!sessionStorage.getItem("firstLoginSession")) {
      sessionStorage.setItem("firstLoginSession", "1");
    }
    return { redirectPath: redirectPath || "/main" };
  },
  async fetchAccessToken({ commit, dispatch }, { isRefresh = false } = {}) {
    return new Promise(async (resolve) => {
      try {
        const res = await axios.post(`/auth/access_token`, {}, { withCredentials: true });
        const token = res.data.accessToken;

        commit("setToken", token);
        dispatch("setUserFromToken", token);
        dispatch("setRoleFromToken", token);
        commit("setIsAuthenticate", true);
        if(!sessionStorage.getItem("firstLoginSession") && !isRefresh){
          sessionStorage.setItem("firstLoginSession", "1");
        }

        // Analytics must not affect auth state. ipify.org can be blocked by
        // ad-blockers / corporate networks (response interceptor would then
        // see a 4xx/5xx and the outer catch would reset isAuthenticate to
        // false, logging the user out on every refresh). Same risk for
        // window._paq / Vue.prototype.$gtag if not yet initialised.
        try {
          const decodeValue = jwt_decode(token);
          if (decodeValue.login_by === "digital_id") {
            window._paq.push(["setUserId", `DI-${decodeValue.sub}`]);
          } else if (decodeValue.login_by === "dbd") {
            window._paq.push(["setUserId", `DBD-${decodeValue.sub}`]);
          }

          const ipRes = await axios.get("https://api.ipify.org/?format=json");
          Vue.prototype.$gtag.customMap({
            user_infonation: "session",
          });

          Vue.prototype.$gtag.event("session", {
            type: "page_open",
            ip_address: ipRes.data.ip,
            username: decodeValue.sub,
          });

          Vue.prototype.$gtag.event("page_open", {
            event_category: "session",
            event_label: "ip_address",
            value: ipRes.data.ip,
          });

          Vue.prototype.$gtag.event("page_open", {
            event_category: "session",
            event_label: "username",
            value: decodeValue.sub,
          });
        } catch (analyticsErr) {
          // eslint-disable-next-line no-console
          console.warn(
            "[fetchAccessToken] analytics call failed (non-fatal):",
            analyticsErr && analyticsErr.message ? analyticsErr.message : analyticsErr
          );
        }

        return resolve(token);
      } catch (e) {
        if (e.response) {
          commit("setIsAuthenticate", false);
        }
        return resolve(null);
      }
    });
  },
  async logout({ commit }) {
    return new Promise(async (resolve) => {
      try {
        commit("clearUser");
        commit("clearRole");
        commit("clearToken");
        commit("setIsAuthenticate", false);
        localStorage.setItem("redirectAfterLogout", "/main");
        window.location.href = `${process.env.VUE_APP_SMEGP_BASE_API}/auth/logout?backToPath=${window.location.origin}`;
        resolve();
      } catch (error) {
        commit("clearUser");
        commit("clearRole");
        commit("clearToken");
        commit("setIsAuthenticate", false);
        resolve();
      }
    });
  },
};

export const getters = {
  getRoles() {
    return state.role && state.role.length > 0 ? state.role.map((item) => item.name) : [];
  },
  getUser() {
    return state.user;
  },
  getToken() {
    return state.accessToken;
  },
};
